import { useEffect, useMemo, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CountryCode } from 'libphonenumber-js'
import './style.scss'
import utility from 'src/utils/utility'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'
import {
    Header, BackLink, Card, Button, TextField, PhoneField,
    InfoBanner, CircularProgress, Text, Flex, Box, tokens, TeamList
} from 'src/componentv2'
import { SelectField } from 'src/componentv2/Base/SelectField'
import { Notification } from 'src/componentv2/Base/Notification'
import type { ToastState } from 'src/model/v2/interface/props'
import type { TeammateInviteRequest, ResendTeammateInviteRequest } from 'src/model/v2/interface/params'
import { PAGE_PADDING_X, fontRegular, ROLE_OPTIONS, ROLE_LABEL,
} from 'src/model/v2/interface/constants'
import {
    makeFieldError, makePhoneFieldError, isFormValid as checkFormValid,
} from 'src/app/dashboardv2/function'

interface FormState {
    firstName: string
    lastName: string
    userName: string
    email: string
    country: CountryCode
    phone: string
    role: string
}

const EMPTY_FORM: FormState = {
    firstName: '', lastName: '',
    userName: '', email: '',
    country: 'NG', phone: '',
    role: '',
}

const InviteTeam = () => {
    const navigate = useNavigate()
    const {
        teammates,
        teammatesMeta,
        getTeammates,
        inviteTeammate,
        resendTeammateInvite,
    } = useContext(ConfigurationApiContext)

    const [form, setForm] = useState<FormState>(EMPTY_FORM)

    const [page, setPage] = useState(1)
    const [pageLoading, setPageLoading] = useState(false)

    const [toast, setToast] = useState<ToastState | null>(null)
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [inviting, setInviting] = useState(false)
    const [resendingId, setResendingId] = useState<string | number | null>(null)

    const [phoneValid, setPhoneValid] = useState(true)

    useEffect(() => {
        getTeammates(1)
    }, [])

    const handlePageChange = async (nextPage: number) => {
        if (nextPage === page || pageLoading) return
        setPageLoading(true)
        await getTeammates(nextPage)
        setPage(nextPage)
        setPageLoading(false)
    }

    const progress = teammates.length ? 75 : 50

    const errors = useMemo(() => ({
        firstName: !form.firstName,
        lastName: !form.lastName,
        userName: !form.userName,
        email: !form.email,
        phone: !form.phone || !phoneValid,
        role: !form.role,
    }), [form, phoneValid])

    const isFormValid = useMemo(() => checkFormValid(errors), [errors])

    const set = <K extends keyof FormState>(key: K) => (value: FormState[K]) =>
        setForm((f) => ({ ...f, [key]: value }))

    const fieldError = (key: keyof typeof errors) =>
        makeFieldError(submitAttempted, errors[key])

    const phoneFieldError = () => makePhoneFieldError(submitAttempted, form.phone)

    const handleInvite = async () => {
        if (!isFormValid) {
            setSubmitAttempted(true)
            return
        }

        const payload: TeammateInviteRequest = {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            username: form.userName,
            phone: form.phone,
            role: form.role,
        }

        setInviting(true)
        const created = await inviteTeammate(payload)
        setInviting(false)

        if (!created) return

        setForm(EMPTY_FORM)
        setPhoneValid(true)
        setSubmitAttempted(false)
        setPage(1)
        setToast({ message: 'Team Member Invited Successfully', variant: 'success' })
    }

    const handleResend = async (member: any) => {
        setResendingId(member.id)
        const payload: ResendTeammateInviteRequest = { userId: member.id }
        const ok = await resendTeammateInvite(payload)
        setResendingId(null)

        setToast(
            ok
                ? { message: `Invite resent to ${member.email}`, variant: 'success' }
                : { message: 'Could not resend invite, try again', variant: 'error' }
        )
    }

    return (
        <Box bgColor={tokens.pageBg} minHeight="100vh">
            <Header sticky initials="QO" />

            {toast && (
                <div className="invite-team__toast">
                    <Notification
                        message={toast.message}
                        actionLabel="Dismiss"
                        onClose={() => setToast(null)}
                        position="inline"
                        variant={toast.variant}
                    />
                </div>
            )}

            <div className="invite-team-page" style={{ padding: `3.2rem ${PAGE_PADDING_X}` }}>
                <BackLink onClick={() => navigate(-1)}>
                    Go back
                </BackLink>

                <Flex
                    justify="space-between"
                    items="flex-start"
                    topMargin="1.6"
                    gap="1.6"
                >
                    <Box className="invite-team-header">
                        <Text fontSize="2.4" fontWeight="700">
                            Invite Team Members
                        </Text>

                        <Text
                            topMargin="0.6"
                            fontSize="1.4"
                            color={tokens.textMuted}
                        >
                            Enable secure collaboration and operational management.
                        </Text>
                    </Box>

                    <CircularProgress
                        value={progress}
                        caption="Step 3 of 4"
                    />
                </Flex>

                {/* ── Invite form card ─────────────────────────────────────── */}
                <Card topMargin="2" padding="3.2" radius="1.2">
                    <InfoBanner className="institution-info-banner">
                        We recommend adding at least one additional admin for continuity and accountability.
                    </InfoBanner>
                    <div className="form-row" style={{ marginTop: '2rem' }}>
                        <TextField
                            label="First Name"
                            placeholder="Enter first name"
                            error={fieldError('firstName')}
                            value={form.firstName}
                            onChange={set('firstName')}
                        />
                        <TextField
                            label="Last Name"
                            placeholder="Enter last name"
                            error={fieldError('lastName')}
                            value={form.lastName}
                            onChange={set('lastName')}
                        />
                    </div>

                    <div className="form-row">
                        <TextField
                            label="User Name"
                            placeholder="Enter username"
                            error={fieldError('userName')}
                            value={form.userName}
                            onChange={set('userName')}
                        />
                        <TextField
                            label="Email Address"
                            placeholder="Enter email address"
                            type="email"
                            error={fieldError('email')}
                            value={form.email}
                            onChange={set('email')}
                        />
                    </div>

                    <div className="form-row">
                        <PhoneField
                            label="Phone Number"
                            country={form.country}
                            onCountryChange={set('country')}
                            error={phoneFieldError()}
                            value={form.phone}
                            onChange={set('phone')}
                            onValidityChange={setPhoneValid}
                        />
                        <SelectField
                            label="Role"
                            placeholder="Select user role"
                            error={fieldError('role')}
                            value={form.role}
                            onChange={set('role')}
                            options={ROLE_OPTIONS}
                        />
                    </div>

                    <Flex direction="column" items="flex-end" topMargin="2.4">
                        <Button
                            variant="primary"
                            size="md"
                            disabled={!isFormValid || inviting}
                            onClick={handleInvite}
                        >
                            {inviting ? 'Inviting…' : 'Invite'}
                        </Button>

                        {submitAttempted && !isFormValid && (
                            <Text fontSize="1.3" topMargin="0.8" color="#D92D20" style={fontRegular}>
                                Please check the highlighted fields, something's missing.
                            </Text>
                        )}
                    </Flex>
                </Card>

                {/* ── Team members list ────────────────────────────────────── */}
                <TeamList
                    members={teammates}
                    roleLabels={ROLE_LABEL}
                    resendingId={resendingId}
                    meta={teammatesMeta}
                    onPageChange={handlePageChange}
                    pageLoading={pageLoading}
                    onAdd={() =>
                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth',
                        })
                    }
                    onResend={handleResend}
                />
            </div>
        </Box>
    )
}

export default utility.routeData('invite-team', 'Invite Team Members', <InviteTeam />)